import type { ChatResponse } from '@/types';
import { getAuthToken, handleUnauthorized } from '@/lib/auth';

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v1`;

type StreamErrorPayload = {
  error?: string;
  detail?: string;
  message?: string;
  explanation?: string;
  status_code?: number;
};

export type StreamOperationPayload = {
  op?: string;
  title?: string;
  detail?: string;
  status?: string;
};

export type ChatStreamEvent =
  | { type: 'connected'; payload: Record<string, unknown> }
  | { type: 'stage'; payload: Record<string, unknown> }
  | { type: 'thinking'; payload: { text?: string } }
  | { type: 'operation'; payload: StreamOperationPayload }
  | { type: 'result'; payload: ChatResponse }
  | { type: 'error'; payload: StreamErrorPayload }
  | { type: 'done'; payload: Record<string, unknown> }
  | { type: 'unknown'; event: string; payload: unknown };

export interface StreamChatOptionParams {
  input: string;
  session_id?: string;
  publicMode?: boolean;
  signal?: AbortSignal;
  onEvent?: (event: ChatStreamEvent) => void;
}

function parseEventBlock(block: string): { event: string; data: string } | null {
  const lines = block.split(/\r?\n/);
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return { event, data: dataLines.join('\n') };
}

function splitSseBlocks(chunkBuffer: string): { blocks: string[]; remainder: string } {
  const blocks = chunkBuffer.split(/\r?\n\r?\n/);
  return {
    blocks: blocks.slice(0, -1),
    remainder: blocks[blocks.length - 1] ?? '',
  };
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function normalizeEvent(event: string, payload: unknown): ChatStreamEvent {
  if (event === 'connected') {
    return { type: 'connected', payload: (payload as Record<string, unknown>) ?? {} };
  }
  if (event === 'stage') {
    return { type: 'stage', payload: (payload as Record<string, unknown>) ?? {} };
  }
  if (event === 'thinking') {
    return { type: 'thinking', payload: (payload as { text?: string }) ?? {} };
  }
  if (event === 'operation') {
    return { type: 'operation', payload: (payload as StreamOperationPayload) ?? {} };
  }
  if (event === 'result') {
    return { type: 'result', payload: payload as ChatResponse };
  }
  if (event === 'error') {
    return { type: 'error', payload: (payload as StreamErrorPayload) ?? {} };
  }
  if (event === 'done') {
    return { type: 'done', payload: (payload as Record<string, unknown>) ?? {} };
  }
  return { type: 'unknown', event, payload };
}

export async function streamChatOption(
  params: StreamChatOptionParams
): Promise<ChatResponse> {
  const { input, session_id, publicMode = false, signal, onEvent } = params;

  const query = session_id ? `?session_id=${encodeURIComponent(session_id)}` : '';
  const token = getAuthToken();
  const path = publicMode ? 'public/chat/stream' : 'chat/stream';
  const response = await fetch(`${API_BASE_URL}/${path}${query}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(!publicMode && token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ input }),
    signal,
  });

  if (!response.ok) {
    if (!publicMode && response.status === 401) {
      handleUnauthorized();
    }
    const bodyText = await response.text().catch(() => '');
    throw new Error(
      `Streaming request failed (${response.status}): ${bodyText || response.statusText}`
    );
  }

  if (!response.body) {
    throw new Error('Streaming response body is empty.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let resultPayload: ChatResponse | null = null;
  let streamError: StreamErrorPayload | null = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const { blocks, remainder } = splitSseBlocks(buffer);
    buffer = remainder;

    for (const block of blocks) {
      const parsed = parseEventBlock(block);
      if (!parsed) {
        continue;
      }

      const payload = safeJsonParse(parsed.data);
      const normalized = normalizeEvent(parsed.event, payload);
      onEvent?.(normalized);

      if (normalized.type === 'result') {
        resultPayload = normalized.payload;
      } else if (normalized.type === 'error') {
        streamError = normalized.payload;
      } else if (normalized.type === 'done') {
        if (streamError) {
          break;
        }
      }
    }

    if (streamError) {
      break;
    }
  }

  if (buffer.trim()) {
    const parsed = parseEventBlock(buffer);
    if (parsed) {
      const payload = safeJsonParse(parsed.data);
      const normalized = normalizeEvent(parsed.event, payload);
      onEvent?.(normalized);

      if (normalized.type === 'result') {
        resultPayload = normalized.payload;
      } else if (normalized.type === 'error') {
        streamError = normalized.payload;
      }
    }
  }

  if (streamError) {
    const message =
      streamError.explanation ||
      streamError.message ||
      streamError.detail ||
      streamError.error ||
      'Streaming request failed.';
    throw new Error(message);
  }

  if (!resultPayload) {
    throw new Error('Stream ended without a result payload.');
  }

  return resultPayload;
}
